import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller; // Declare the controller reference here without initializing

  connectedCallback() {
    super.connectedCallback();
    
    // FIX 1: Initialize a fresh AbortController every time the element connects
    // This ensures event listeners attach correctly even if the element is moved/re-inserted.
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Clean up event listeners by aborting the controller
    this.#controller?.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const sourceHtml = event.detail.data.html;
    if (!sourceHtml) return;

    // FIX 2: Safely parse the incoming HTML string into a queryable DOM Document
    const parser = new DOMParser();
    const doc = parser.parseFromString(sourceHtml, 'text/html');

    const newMediaGallery = doc.querySelector('media-gallery');
    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    // FIX: after swapping in the new server-rendered gallery, explicitly force
    // the slideshow to slide 0 instead of relying on the browser's own scroll
    // position/scroll-snap calculation to determine the "current" slide.
    //
    // Why this matters: some browsers (notably Safari) can compute scroll-snap
    // resting position before lazy-loaded images have reported their final
    // width/height, which can cause the gallery to visually land on the wrong
    // slide on first paint after the swap. Forcing an explicit, index-based
    // selection removes that ambiguity regardless of image load timing.
    //
    // We wait a frame because `replaceWith` connects the new element
    // synchronously, but its own refs (e.g. `slideshow`) may not be fully
    // resolved until the next paint/tick.
    requestAnimationFrame(() => {
      /** @type {import('./slideshow').Slideshow | undefined} */
      const newSlideshow = newMediaGallery.refs?.slideshow;
      newSlideshow?.select(0, undefined, { animate: false });
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}